import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { getTraineeGamificationData } from '../../../services/adminService';

function TraineeAnalyticsGamification({ traineeId }) {
  const [gamificationData, setGamificationData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (traineeId) {
      fetchGamificationData();
    }
  }, [traineeId]);

  const fetchGamificationData = async () => {
    try {
      const response = await getTraineeGamificationData(traineeId);

      // console.log("Gamification Data Response", response);
      
      if (response.success && response.data) {
        setGamificationData(response.data);
      }
    } catch (error) {
      console.error('Error fetching gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level) => {
    if (level >= 5) return 'text-success';
    if (level >= 3) return 'text-warning';
    if (level >= 1) return 'text-info';
    return 'text-muted';
  };

  const getStreakColor = (streak) => {
    if (streak >= 10) return 'text-danger';
    if (streak >= 5) return 'text-warning';
    if (streak >= 1) return 'text-success';
    return 'text-muted';
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-12">
        <div className="col-span-12">
          <div className="bg-white rounded-lg shadow-sm border-0">
            <div className="bg-gradient-primary text-white p-4 rounded-t-lg">
              <h5 className="mb-0">
                <Icon icon="fluent:trophy-24-regular" className="mr-2" />
                Trainee Gamification Analytics
              </h5>
            </div>
            <div className="p-4">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-gray-500">Loading gamification analytics...</p>
                </div>
              ) : gamificationData ? (
    <div>
                  {/* Gamification Stats Cards */}
                  <div className="grid grid-cols-12 gap-4 mb-4">
                    <div className="col-span-12 md:col-span-3 mb-3">
                      <div className="bg-white rounded-lg shadow-sm border-0 h-full" style={{ backgroundColor: '#fff3e0' }}>
                        <div className="p-4 text-center">
                          <div className="rounded-full bg-white p-3 mx-auto mb-3" style={{ width: '60px', height: '60px' }}>
                            <Icon icon="fluent:star-24-regular" width="24" height="24" style={{ color: '#ff9800' }} />
                          </div>
                          <h6 className="text-gray-500 mb-1">Total Points</h6>
                          <h3 className="mb-0 font-bold text-yellow-500">
                            {gamificationData.userData?.total_points || 0}
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-3 mb-3">
                      <div className="bg-white rounded-lg shadow-sm border-0 h-full" style={{ backgroundColor: '#e8f4fd' }}>
                        <div className="p-4 text-center">
                          <div className="rounded-full bg-white p-3 mx-auto mb-3" style={{ width: '60px', height: '60px' }}>
                            <Icon icon="fluent:target-24-regular" width="24" height="24" style={{ color: '#1976d2' }} />
                          </div>
                          <h6 className="text-gray-500 mb-1">Current Level</h6>
                          <h3 className="mb-0 font-bold text-primary">
                            {gamificationData.userData?.current_level || 1}
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-3 mb-3">
                      <div className="bg-white rounded-lg shadow-sm border-0 h-full" style={{ backgroundColor: '#fce4ec' }}>
                        <div className="p-4 text-center">
                          <div className="rounded-full bg-white p-3 mx-auto mb-3" style={{ width: '60px', height: '60px' }}>
                            <Icon icon="fluent:trophy-24-regular" width="24" height="24" style={{ color: '#e91e63' }} />
                          </div>
                          <h6 className="text-gray-500 mb-1">Total Badges</h6>
                          <h3 className="mb-0 font-bold text-red-500">
                            {gamificationData.userData?.total_badges || 0}
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-3 mb-3">
                      <div className="bg-white rounded-lg shadow-sm border-0 h-full" style={{ backgroundColor: '#e8f5e8' }}>
                        <div className="p-4 text-center">
                          <div className="rounded-full bg-white p-3 mx-auto mb-3" style={{ width: '60px', height: '60px' }}>
                            <Icon icon="fluent:fire-24-regular" width="24" height="24" style={{ color: '#4caf50' }} />
                          </div>
                          <h6 className="text-gray-500 mb-1">Current Streak</h6>
                          <h3 className="mb-0 font-bold text-green-500">
                            {gamificationData.userData?.current_streak || 0} 🔥
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Badges Section */}
                  <div className="grid grid-cols-12 gap-4 mb-4">
                    <div className="col-span-12">
                      <div className="bg-white rounded-lg shadow-sm border-0">
                        <div className="p-4 border-b">
                          <h6 className="mb-0">
                            <Icon icon="fluent:trophy-24-regular" className="mr-2" />
                            Earned Badges ({gamificationData.userBadges?.total_earned || 0} / {gamificationData.allBadges?.length || 0})
                          </h6>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-12 gap-4">
                            {gamificationData.allBadges?.map((badge) => {
                              const isEarned = gamificationData.userBadges?.earned_badges?.some(ub => ub.badge_id === badge.id);
                              return (
                                <div key={badge.id} className="col-span-12 md:col-span-3 mb-3">
                                  <div className={`bg-white rounded-lg text-center h-full ${isEarned ? 'border-green-500' : 'border-gray-500'}`}
                                    style={{
                                      backgroundColor: isEarned ? '#d4edda' : '#e9ecef',
                                      border: isEarned ? '2px solid #28a745' : '2px solid #6c757d',
                                      opacity: isEarned ? 1 : 0.8,
                                      minHeight: '150px'
                                    }}>
                                    <div className="p-3">
                                      <div className="mb-2">
                                        <Icon
                                          icon="fluent:trophy-24-regular"
                                          width="32"
                                          height="32"
                                          style={{
                                            color: isEarned ? '#28a745' : '#6c757d'
                                          }}
                                        />
                                      </div>
                                      <h6 className="mb-2" style={{ fontSize: '0.9rem' }}>
                                        {badge.badges_name.replace(' Badge', '')}
                                      </h6>
                                      <div className="mt-auto">
                                        {isEarned ? (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500 text-white p-2">
                                            Earned
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-500 text-white p-2">
                                            Not Earned
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Levels Section */}
                  <div className="grid grid-cols-12">
                    <div className="col-span-12">
                      <div className="bg-white rounded-lg shadow-sm border-0">
                        <div className="p-4 border-b">
                          <h6 className="mb-0">
                            <Icon icon="fluent:target-24-regular" className="mr-2" />
                            Level Progress
                          </h6>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-12 gap-4">
                            {gamificationData.levels?.map((level) => (
                              <div key={level.id} className="col-span-12 md:col-span-2 mb-3">
                                <div className={`bg-white rounded-lg text-center h-full ${
                                  level.level === gamificationData.userData?.current_level 
                                    ? 'border-primary border-2 shadow' 
                                    : 'border-0 shadow-sm'
                                }`}>
                                  <div className="p-3">
                                    <div className="mb-2">
                                      <div className={`rounded-full mx-auto flex items-center justify-center ${
                                        level.level === gamificationData.userData?.current_level 
                                          ? 'bg-primary' 
                                          : 'bg-gray-100'
                                      }`}
                                      style={{
                                        width: '40px',
                                        height: '40px'
                                      }}>
                                        <Icon
                                          icon="fluent:target-24-regular"
                                          width="20"
                                          height="20"
                                          className={level.level === gamificationData.userData?.current_level ? 'text-white' : 'text-gray-500'}
                                        />
                                      </div>
                                    </div>
                                    <h6 className={`mb-1 ${level.level === gamificationData.userData?.current_level ? 'text-primary' : 'text-gray-500'}`}>
                                      Level {level.level}
                                    </h6>
                                    <p className={`text-sm ${level.level === gamificationData.userData?.current_level ? 'text-primary' : 'text-gray-500'}`}>
                                      {level.name}
                                    </p>
                                    {level.level === gamificationData.userData?.current_level && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-subtle text-primary">
                                        Current
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="text-gray-500">
                    <Icon icon="fluent:trophy-24-regular" width="48" height="48" className="mb-3" />
                    <h6>No gamification data available</h6>
                    <p className="mb-0">This trainee hasn't earned any gamification data yet.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TraineeAnalyticsGamification;
